import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Loader2, CornerDownRight } from 'lucide-react';
import { NODE_TYPE_LABELS } from './types';
import type { PendingMove } from './useOrgChartDnd';

interface MoveConfirmDialogProps {
  move: PendingMove | null;
  saving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirms a drag-and-drop reparent before it is saved.
 *
 * A drag is easy to start by accident, and a move rewrites the hierarchy for
 * everything underneath it — so the change is held here, named in full, until
 * it is explicitly accepted.
 *
 * @param move - The pending move, or null when nothing is awaiting confirmation
 * @param saving - True while the move is being persisted
 */
export function MoveConfirmDialog({ move, saving, onConfirm, onCancel }: MoveConfirmDialogProps) {
  if (!move) return null;

  const movedLabel = NODE_TYPE_LABELS[move.node.type].toLowerCase();
  const childCount = move.node.children?.length ?? 0;

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move this {movedLabel}?</DialogTitle>
          <DialogDescription>
            This changes where it sits in the hierarchy. It can be moved back afterwards.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <div className="font-medium">{move.node.name}</div>
          <div className="mt-2 flex items-start gap-2 text-muted-foreground">
            <CornerDownRight className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              from <span className="font-medium text-foreground">{move.fromName}</span>
              {' '}into <span className="font-medium text-foreground">{move.toName}</span>
            </span>
          </div>
          {childCount > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              {childCount} item{childCount === 1 ? '' : 's'} inside will move with it.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
